import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { MasterDataService } from '../../../../service/core/master-data.service';

/** What the host receives when the user confirms (or skips) the loss dialog. */
export interface LossReasonResult {
    reasonCode: string | null;
    reasonNote: string;
}

/**
 * "Why was this deal lost?" — shown by the status pickers when the chosen
 * status is terminal and not won. Reasons come from the LOSS_REASON
 * master-data category (Settings → Master Data); free-text note is optional.
 * Skipping still applies the status — capturing the reason is encouraged,
 * never a gate. Feeds the loss-cause analytics.
 */
@Component({
    selector: 'app-loss-reason-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, ButtonModule, SelectModule, TextareaModule],
    template: `
    <p-dialog [visible]="visible" (visibleChange)="onVisibleChange($event)" [modal]="true"
              [draggable]="false" [resizable]="false" appendTo="body"
              [style]="{ width: '26rem' }" header="Why was this deal lost?">
        <div class="lrd">
            <p class="lrd__hint">
                Marking as <strong>{{ statusName || 'lost' }}</strong> — a quick reason powers
                your loss analysis. Optional.
            </p>
            <label class="lrd__label">Reason</label>
            <p-select [filter]="true" filterPlaceholder="Search" [options]="reasonOptions" [(ngModel)]="selectedCode"
                      optionLabel="label" optionValue="value" [showClear]="true"
                      placeholder="Pick a reason" styleClass="w-full" appendTo="body"></p-select>
            <label class="lrd__label">Note (optional)</label>
            <textarea pTextarea [(ngModel)]="note" rows="2" class="lrd__note"
                      placeholder="Any detail worth remembering…"></textarea>
        </div>
        <ng-template pTemplate="footer">
            <button pButton label="Skip" class="p-button-text p-button-sm" (click)="skip()"></button>
            <button pButton label="Save" icon="pi pi-check" class="p-button-sm" (click)="confirm()"></button>
        </ng-template>
    </p-dialog>
    `,
    styles: [`
        .lrd { display: flex; flex-direction: column; gap: 6px; }
        .lrd__hint { margin: 0 0 6px; font-size: var(--gh-fs-14); color: var(--gh-text-3); }
        .lrd__label { font-size: var(--gh-fs-12); font-weight: 700; color: var(--gh-text-2); text-transform: uppercase; letter-spacing: .04em; margin-top: 4px; }
        .lrd__note { width: 100%; border: 1px solid var(--gh-border); border-radius: var(--gh-r-sm); padding: 8px 10px; font: inherit; }
        .lrd__note:focus { outline: none; border-color: var(--gh-primary); }
        .w-full { width: 100%; }
    `],
})
export class LossReasonDialogComponent implements OnChanges {

    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** Display name of the lost status ("Closed Lost") — shown in the hint. */
    @Input() statusName = '';

    /** Fires exactly once per open — on Save (with values) or Skip (nulls). */
    @Output() confirmed = new EventEmitter<LossReasonResult>();

    reasonOptions: { label: string; value: string }[] = [];
    selectedCode: string | null = null;
    note = '';
    private loaded = false;

    constructor(private masterData: MasterDataService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible) {
            this.selectedCode = null;
            this.note = '';
            if (!this.loaded) this.loadReasons();
        }
    }

    private loadReasons(): void {
        this.masterData.list('LOSS_REASON', true).subscribe({
            next: (rows) => {
                this.reasonOptions = (rows || []).map(r => ({ label: r.label, value: r.code }));
                this.loaded = true;
            },
            error: () => { /* keep empty — free-text note still works */ },
        });
    }

    onVisibleChange(v: boolean): void {
        this.visible = v;
        this.visibleChange.emit(v);
        // Closing via X / escape counts as skip so the host always proceeds.
        if (!v) this.confirmed.emit({ reasonCode: null, reasonNote: '' });
    }

    confirm(): void {
        const result: LossReasonResult = { reasonCode: this.selectedCode, reasonNote: (this.note || '').trim() };
        this.close();
        this.confirmed.emit(result);
    }

    skip(): void {
        this.close();
        this.confirmed.emit({ reasonCode: null, reasonNote: '' });
    }

    private close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }
}
